import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import {
  api,
  type DashboardNextAction,
  type DashboardSnapshot,
  type DashboardTrendSeriesKey,
} from '@/lib/api'
import { getAuthState, isAuthenticated } from '@/lib/auth-store'
import styles from './home-dashboard.module.css'

export const Route = createFileRoute('/home')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: HomeRoute,
})

const TREND_CHART_WIDTH = 760
const TREND_CHART_HEIGHT = 220
const TREND_CHART_PADDING_X = 44
const TREND_CHART_PADDING_Y = 18

const TREND_SERIES_STYLES: Record<DashboardTrendSeriesKey, { label: string; color: string }> = {
  connections: { label: 'Connections', color: '#0ea5e9' },
  totalEngagements: { label: 'Total engagements', color: '#0891b2' },
  likes: { label: 'Likes', color: '#14b8a6' },
  comments: { label: 'Comments', color: '#f59e0b' },
  shares: { label: 'Shares', color: '#a855f7' },
}

const FALLBACK_ACTION: DashboardNextAction = {
  id: 'draft',
  title: 'Create 3 new drafts before your queue runs dry',
  detail: 'Load your latest workspace data to get your exact next action.',
  userLead: 'Create three voice-safe drafts in one focused block.',
  ctaLabel: 'Generate drafts',
  to: '/workspace',
  impactLabel: 'Protects visibility for the next 7 days.',
  queueCategory: 'Create',
  queueMinutes: 8,
  rationale: 'Draft queue depth is below the weekly execution target.',
  confidence: 0.62,
  expectedImpact: 64,
  sourceSignals: ['draft_queue=0', 'scheduled=0/3'],
  timeframe: 'next 24 hours',
  freshness: {
    isFresh: false,
    staleDomains: ['profile', 'posts', 'metrics', 'responders'],
    lastSyncAt: null,
  },
}

function formatSignedNumber(value: number): string {
  return `${value > 0 ? '+' : ''}${value}`
}

function computeTicks(maxValue: number): number[] {
  const safeMax = Math.max(1, maxValue)
  return [safeMax, Math.round(safeMax * 0.66), Math.round(safeMax * 0.33), 0]
}

function buildLinePath(values: number[], maxValue: number): string {
  if (values.length === 0) {
    return ''
  }

  const safeMax = Math.max(1, maxValue)

  return values
    .map((value, index) => {
      const x = chartX(index, values.length)
      const y = chartY(value, safeMax)
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(' ')
}

function chartX(index: number, totalPoints: number): number {
  const innerWidth = TREND_CHART_WIDTH - TREND_CHART_PADDING_X * 2
  return totalPoints <= 1
    ? TREND_CHART_PADDING_X
    : TREND_CHART_PADDING_X + (index / (totalPoints - 1)) * innerWidth
}

function chartY(value: number, maxValue: number): number {
  const innerHeight = TREND_CHART_HEIGHT - TREND_CHART_PADDING_Y * 2
  const safeMax = Math.max(1, maxValue)
  return TREND_CHART_PADDING_Y + (1 - value / safeMax) * innerHeight
}

function HomeRoute() {
  const auth = getAuthState()
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [trendVisibility, setTrendVisibility] = useState<Record<DashboardTrendSeriesKey, boolean>>({
    connections: true,
    totalEngagements: true,
    likes: true,
    comments: true,
    shares: true,
  })

  useEffect(() => {
    const loadSnapshot = async (): Promise<void> => {
      try {
        const data = await api.getDashboardSnapshot()
        setSnapshot(data)
        setErrorMessage(null)
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to load dashboard snapshot.'
        )
      }
    }

    void loadSnapshot()
  }, [])

  const metrics = snapshot?.metrics ?? {
    scheduled: 0,
    published: 0,
    queue: 0,
    warmLeads: 0,
    highIntent: 0,
    responseNeeded: 0,
    cadenceTarget: 3,
  }

  const immediateAction = snapshot?.immediateAction ?? FALLBACK_ACTION
  const priorityQueue = snapshot?.priorityQueue ?? []
  const queueMinutes = snapshot?.queueMinutes ?? 0
  const systemNextSteps = snapshot?.systemNextSteps ?? []
  const cadenceCoverage = snapshot?.cadenceCoverage ?? 0
  const cadenceGap = snapshot?.cadenceGap ?? metrics.cadenceTarget
  const advisorCards = snapshot?.advisorCards ?? []
  const warmLeadRadar = snapshot?.warmLeadRadar ?? []
  const responseInbox = snapshot?.responseInbox ?? []
  const impactDrivers = snapshot?.impactDrivers ?? []
  const trendPoints = snapshot?.trend.points ?? []
  const trendDelta =
    snapshot?.trend.delta ??
    ({
      connections: 0,
      totalEngagements: 0,
      likes: 0,
      comments: 0,
      shares: 0,
    } as Record<DashboardTrendSeriesKey, number>)

  const connectionValues = useMemo(() => {
    return trendPoints.map((point) => point.connections)
  }, [trendPoints])

  const engagementSeriesKeys = useMemo<
    Array<'totalEngagements' | 'likes' | 'comments' | 'shares'>
  >(() => {
    return (['totalEngagements', 'likes', 'comments', 'shares'] as const).filter(
      (seriesKey) => trendVisibility[seriesKey]
    )
  }, [trendVisibility])

  const engagementValuesBySeries = useMemo(() => {
    return {
      totalEngagements: trendPoints.map((point) => point.totalEngagements),
      likes: trendPoints.map((point) => point.likes),
      comments: trendPoints.map((point) => point.comments),
      shares: trendPoints.map((point) => point.shares),
    }
  }, [trendPoints])

  const connectionMax = useMemo(() => {
    return Math.max(...connectionValues, 1)
  }, [connectionValues])

  const engagementMax = useMemo(() => {
    if (engagementSeriesKeys.length === 0) {
      return 1
    }

    return Math.max(
      ...engagementSeriesKeys.flatMap((seriesKey) => engagementValuesBySeries[seriesKey]),
      1
    )
  }, [engagementSeriesKeys, engagementValuesBySeries])

  const connectionPath = useMemo(() => {
    if (!trendVisibility.connections) {
      return ''
    }

    return buildLinePath(connectionValues, connectionMax)
  }, [connectionMax, connectionValues, trendVisibility.connections])

  const engagementPaths = useMemo(() => {
    return engagementSeriesKeys.map((seriesKey) => ({
      key: seriesKey,
      path: buildLinePath(engagementValuesBySeries[seriesKey], engagementMax),
    }))
  }, [engagementMax, engagementSeriesKeys, engagementValuesBySeries])

  const trendTicks = useMemo(() => {
    return {
      connections: computeTicks(connectionMax),
      engagements: computeTicks(engagementMax),
    }
  }, [connectionMax, engagementMax])

  const xTickIndexes = useMemo(() => {
    if (trendPoints.length <= 2) {
      return trendPoints.map((_point, index) => index)
    }

    return [0, Math.floor((trendPoints.length - 1) / 2), trendPoints.length - 1]
  }, [trendPoints])

  const freshnessLabel = snapshot?.generatedAt
    ? new Date(snapshot.generatedAt).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Loading'

  const syncStatus = errorMessage ? 'Needs attention' : (snapshot?.syncStatus ?? 'Healthy')
  const toggleTrendSeries = (seriesKey: DashboardTrendSeriesKey): void => {
    setTrendVisibility((current) => ({
      ...current,
      [seriesKey]: !current[seriesKey],
    }))
  }

  return (
    <section className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroContent}>
          <p className={styles.overline}>Execution workspace</p>
          <h1 className={styles.heroTitle}>{immediateAction.title}</h1>
          <p className={styles.heroSubtitle}>
            Signed in as <strong>{auth.user?.name ?? auth.user?.email}</strong>.{' '}
            {immediateAction.detail}
          </p>
          <div className={styles.heroActions}>
            <Link to={immediateAction.to} className={styles.primaryAction}>
              {immediateAction.ctaLabel}
            </Link>
            <p className={styles.heroImpact}>
              Expected impact: <strong>{immediateAction.impactLabel}</strong>
            </p>
          </div>
        </div>

        <div className={styles.impactGrid}>
          <article className={styles.impactCard}>
            <p className={styles.impactLabel}>Cadence coverage</p>
            <p className={styles.impactValue}>{metrics.scheduled}/3</p>
            <p className={styles.impactHint}>
              {cadenceGap === 0
                ? 'This week is fully covered.'
                : `${cadenceGap} slot${cadenceGap === 1 ? '' : 's'} open.`}
            </p>
          </article>
          <article className={styles.impactCard}>
            <p className={styles.impactLabel}>Warm responders</p>
            <p className={styles.impactValue}>{metrics.warmLeads}</p>
            <p className={styles.impactHint}>{metrics.highIntent} high-intent contacts ready.</p>
          </article>
          <article className={styles.impactCard}>
            <p className={styles.impactLabel}>20-minute loop</p>
            <p className={styles.impactValue}>{queueMinutes} min</p>
            <p className={styles.impactHint}>{priorityQueue.length} priority tasks in queue.</p>
          </article>
        </div>
      </header>

      {errorMessage && <p className={styles.errorBanner}>{errorMessage}</p>}

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <p className={styles.panelEyebrow}>Today's workflow</p>
            <h2 className={styles.panelTitle}>Clear execution lanes</h2>
          </div>
          <p className={styles.muted}>{advisorCards.length} active recommendation signals</p>
        </div>

        <div className={styles.executionSplit}>
          <article className={styles.lane}>
            <p className={styles.laneTitle}>You do next</p>
            <p className={styles.laneLead}>{immediateAction.userLead}</p>
            <p className={styles.laneCopy}>{immediateAction.detail}</p>
            <Link to={immediateAction.to} className={styles.inlineAction}>
              {immediateAction.ctaLabel}
            </Link>
          </article>

          <article className={styles.lane}>
            <p className={styles.laneTitle}>StatureBoost does next for you</p>
            <ul className={styles.laneList}>
              {systemNextSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <p className={styles.panelEyebrow}>Impact over time</p>
            <h2 className={styles.panelTitle}>Connections and engagement trends</h2>
          </div>
          <p className={styles.muted}>Toggle metrics for a clearer read by signal type</p>
        </div>

        <div className={styles.trendToggleRow}>
          {(
            [
              'connections',
              'totalEngagements',
              'likes',
              'comments',
              'shares',
            ] as DashboardTrendSeriesKey[]
          ).map((seriesKey) => (
            <button
              key={seriesKey}
              type="button"
              className={
                trendVisibility[seriesKey] ? styles.trendToggleActive : styles.trendToggleButton
              }
              onClick={() => toggleTrendSeries(seriesKey)}
            >
              <span
                className={styles.trendSwatch}
                style={{ backgroundColor: TREND_SERIES_STYLES[seriesKey].color }}
              />
              {TREND_SERIES_STYLES[seriesKey].label}
              <span className={styles.trendToggleDelta}>
                {formatSignedNumber(trendDelta[seriesKey])}
              </span>
            </button>
          ))}
        </div>

        <div className={styles.trendCharts}>
          <article className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3>Connections</h3>
              <p>
                {trendPoints[trendPoints.length - 1]?.connections ?? 0} now •{' '}
                {formatSignedNumber(trendDelta.connections)} in 30 days
              </p>
            </div>

            {trendVisibility.connections ? (
              <div className={styles.chartFrame}>
                <svg
                  viewBox={`0 0 ${TREND_CHART_WIDTH} ${TREND_CHART_HEIGHT}`}
                  className={styles.chartSvg}
                  role="img"
                  aria-label="Connections over time chart"
                >
                  {trendTicks.connections.map((tick) => (
                    <g key={`connection_tick_${tick}`}>
                      <line
                        x1={TREND_CHART_PADDING_X}
                        x2={TREND_CHART_WIDTH - TREND_CHART_PADDING_X}
                        y1={chartY(tick, connectionMax)}
                        y2={chartY(tick, connectionMax)}
                        className={styles.chartGridLine}
                      />
                      <text
                        x={8}
                        y={chartY(tick, connectionMax) + 4}
                        className={styles.chartAxisLabel}
                      >
                        {tick}
                      </text>
                    </g>
                  ))}

                  <path
                    d={connectionPath}
                    fill="none"
                    stroke={TREND_SERIES_STYLES.connections.color}
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {xTickIndexes.map((index) => (
                    <text
                      key={`connections_x_${index}`}
                      x={chartX(index, trendPoints.length)}
                      y={TREND_CHART_HEIGHT - 4}
                      textAnchor={
                        index === 0 ? 'start' : index === trendPoints.length - 1 ? 'end' : 'middle'
                      }
                      className={styles.chartAxisLabel}
                    >
                      {trendPoints[index]?.dateLabel}
                    </text>
                  ))}
                </svg>
              </div>
            ) : (
              <p className={styles.chartEmpty}>Connections are hidden. Toggle it back on above.</p>
            )}
          </article>

          <article className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <h3>Engagement mix</h3>
              <p>
                {engagementSeriesKeys.length} active series •{' '}
                {formatSignedNumber(trendDelta.totalEngagements)} total engagement change
              </p>
            </div>

            {engagementSeriesKeys.length > 0 ? (
              <div className={styles.chartFrame}>
                <svg
                  viewBox={`0 0 ${TREND_CHART_WIDTH} ${TREND_CHART_HEIGHT}`}
                  className={styles.chartSvg}
                  role="img"
                  aria-label="Engagement trend chart with likes comments shares and total"
                >
                  {trendTicks.engagements.map((tick) => (
                    <g key={`engagement_tick_${tick}`}>
                      <line
                        x1={TREND_CHART_PADDING_X}
                        x2={TREND_CHART_WIDTH - TREND_CHART_PADDING_X}
                        y1={chartY(tick, engagementMax)}
                        y2={chartY(tick, engagementMax)}
                        className={styles.chartGridLine}
                      />
                      <text
                        x={8}
                        y={chartY(tick, engagementMax) + 4}
                        className={styles.chartAxisLabel}
                      >
                        {tick}
                      </text>
                    </g>
                  ))}

                  {engagementPaths.map((series) => (
                    <path
                      key={`engagement_path_${series.key}`}
                      d={series.path}
                      fill="none"
                      stroke={TREND_SERIES_STYLES[series.key].color}
                      strokeWidth={series.key === 'totalEngagements' ? 3 : 2.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ))}

                  {xTickIndexes.map((index) => (
                    <text
                      key={`engagement_x_${index}`}
                      x={chartX(index, trendPoints.length)}
                      y={TREND_CHART_HEIGHT - 4}
                      textAnchor={
                        index === 0 ? 'start' : index === trendPoints.length - 1 ? 'end' : 'middle'
                      }
                      className={styles.chartAxisLabel}
                    >
                      {trendPoints[index]?.dateLabel}
                    </text>
                  ))}
                </svg>
              </div>
            ) : (
              <p className={styles.chartEmpty}>Turn on at least one engagement metric above.</p>
            )}
          </article>
        </div>

        <article className={styles.driverPanel}>
          <div className={styles.chartHeader}>
            <h3>Content driving impact</h3>
            <p>Top posts by engagement and connection lift</p>
          </div>
          <div className={styles.driverList}>
            {impactDrivers.map((post) => {
              const postTotal = post.likes + post.comments + post.shares
              return (
                <article key={post.id} className={styles.driverRow}>
                  <div>
                    <p className={styles.driverDate}>{post.publishedAt}</p>
                    <h4 className={styles.driverTitle}>{post.title}</h4>
                    <p className={styles.driverNote}>{post.impactNote}</p>
                  </div>
                  <div className={styles.driverMetrics}>
                    <p>
                      {postTotal} engagements ({post.likes}L / {post.comments}C / {post.shares}S)
                    </p>
                    <p>{formatSignedNumber(post.connectionDelta)} connections</p>
                  </div>
                </article>
              )
            })}
          </div>
        </article>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <p className={styles.panelEyebrow}>Today's priority queue</p>
            <h2 className={styles.panelTitle}>Do these first</h2>
          </div>
          <p className={styles.muted}>{queueMinutes} minutes to complete the loop</p>
        </div>

        <div className={styles.queueGrid}>
          {priorityQueue.map((task) => (
            <article key={task.id} className={styles.queueItem}>
              <div className={styles.queueItemTop}>
                <p className={styles.queueTag}>{task.category}</p>
                <p className={styles.queueMeta}>{task.minutes} min</p>
              </div>
              <h3 className={styles.queueTitle}>{task.title}</h3>
              <p className={styles.queueText}>{task.reason}</p>
              <p className={styles.queueMeta}>Impact: {task.impact}</p>
              <Link to={task.to} className={styles.inlineAction}>
                {task.cta}
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.dualGrid}>
        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>Who to contact</p>
              <h2 className={styles.panelTitle}>Warm lead radar</h2>
            </div>
            <p className={styles.muted}>Top responders ranked by intent and recency</p>
          </div>

          <div className={styles.leadList}>
            {warmLeadRadar.map((lead) => (
              <article key={lead.id} className={styles.leadRow}>
                <div className={styles.leadBody}>
                  <p className={styles.leadName}>{lead.name}</p>
                  <p className={styles.leadMeta}>
                    {lead.headline} at {lead.company}
                  </p>
                  <p className={styles.leadMeta}>{lead.signalSummary}</p>
                </div>
                <div className={styles.leadSide}>
                  <span
                    className={`${styles.intentPill} ${
                      lead.intent === 'High'
                        ? styles.intentHigh
                        : lead.intent === 'Medium'
                          ? styles.intentMedium
                          : styles.intentLow
                    }`}
                  >
                    {lead.intent}
                  </span>
                  <span className={styles.scoreChip}>Score {lead.score}</span>
                </div>
              </article>
            ))}
          </div>
          <Link to="/audience" className={styles.inlineAction}>
            Open full lead queue
          </Link>
        </article>

        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>What needs response</p>
              <h2 className={styles.panelTitle}>Response inbox</h2>
            </div>
            <p className={styles.muted}>Prioritized comment and question threads</p>
          </div>

          <div className={styles.inboxList}>
            {responseInbox.map((lead) => (
              <article key={lead.id} className={styles.inboxRow}>
                <div>
                  <p className={styles.inboxPrimary}>
                    <span className={styles.statusDot} />
                    {lead.name}
                  </p>
                  <p className={styles.inboxSecondary}>{lead.signalSummary}</p>
                  <p className={styles.inboxSecondary}>Suggested move: {lead.recommendedAction}</p>
                </div>
                <p className={styles.queueMeta}>{lead.lastEngagedAt}</p>
              </article>
            ))}
          </div>

          <Link to="/audience" className={styles.inlineAction}>
            Respond from audience queue
          </Link>
        </article>
      </section>

      <section className={styles.dualGrid}>
        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>Consistency health</p>
              <h2 className={styles.panelTitle}>Cadence coverage</h2>
            </div>
            <p className={styles.muted}>{cadenceCoverage}% covered</p>
          </div>

          <div className={styles.cadenceBlock}>
            <p className={styles.laneLead}>{metrics.scheduled}/3 posts scheduled this week</p>
            <p className={styles.laneCopy}>
              {cadenceGap === 0
                ? 'Your posting plan is fully covered.'
                : `Add ${cadenceGap} more scheduled post${cadenceGap === 1 ? '' : 's'} to avoid a visibility dip.`}
            </p>
            <div className={styles.progressTrack} aria-hidden="true">
              <span className={styles.progressFill} style={{ width: `${cadenceCoverage}%` }} />
            </div>
          </div>

          <div className={styles.kpiGrid}>
            <article className={styles.kpiCard}>
              <p className={styles.kpiLabel}>Draft queue</p>
              <p className={styles.kpiValue}>{metrics.queue}</p>
              <p className={styles.kpiTrend}>Ready to schedule</p>
            </article>
            <article className={styles.kpiCard}>
              <p className={styles.kpiLabel}>Published</p>
              <p className={styles.kpiValue}>{metrics.published}</p>
              <p className={styles.kpiTrend}>Recent output</p>
            </article>
          </div>
        </article>

        <article className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <p className={styles.panelEyebrow}>Impact tracking</p>
              <h2 className={styles.panelTitle}>What your actions are driving</h2>
            </div>
          </div>

          <div className={styles.kpiGrid}>
            <article className={styles.kpiCard}>
              <p className={styles.kpiLabel}>High-intent responders</p>
              <p className={styles.kpiValue}>{metrics.highIntent}</p>
              <p className={styles.kpiTrend}>Conversation-ready contacts</p>
            </article>
            <article className={styles.kpiCard}>
              <p className={styles.kpiLabel}>Replies pending</p>
              <p className={styles.kpiValue}>{metrics.responseNeeded}</p>
              <p className={styles.kpiTrend}>Needs response today</p>
            </article>
            <article className={styles.kpiCard}>
              <p className={styles.kpiLabel}>Recommendations live</p>
              <p className={styles.kpiValue}>{advisorCards.length}</p>
              <p className={styles.kpiTrend}>Prescriptive guidance active</p>
            </article>
            <article className={styles.kpiCard}>
              <p className={styles.kpiLabel}>Sync status</p>
              <p className={styles.kpiValue}>{syncStatus}</p>
              <p className={styles.kpiTrend}>Last refresh at {freshnessLabel}</p>
            </article>
          </div>

          <div className={styles.dataRow}>
            <div className={styles.dataItem}>
              <p className={styles.kpiLabel}>Data freshness</p>
              <p className={styles.kpiTrend}>Updated at {freshnessLabel}</p>
            </div>
            <div className={styles.dataItem}>
              <p className={styles.kpiLabel}>Next sync</p>
              <p className={styles.kpiTrend}>
                {errorMessage ? 'Retry required' : 'In ~45 minutes'}
              </p>
            </div>
          </div>
        </article>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <p className={styles.panelEyebrow}>Content opportunities</p>
            <h2 className={styles.panelTitle}>Use your strongest signals</h2>
          </div>
          <p className={styles.muted}>Turn insights into new posts in one click</p>
        </div>

        <div className={styles.opportunityGrid}>
          {advisorCards.map((card) => (
            <article key={card.id} className={styles.opportunityCard}>
              <p className={styles.queueTag}>Signal</p>
              <h3 className={styles.queueTitle}>{card.title}</h3>
              <p className={styles.queueText}>{card.rationale}</p>
              <p className={styles.opportunityImpact}>Impact: {card.impact}</p>
              <Link to={card.actionPath} className={styles.inlineAction}>
                {card.actionLabel}
              </Link>
            </article>
          ))}
        </div>
      </section>
    </section>
  )
}
